import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../../layouts/PageTitle";
import { appApi, useGetSupplierByIdQuery } from "../../../../../store/appApi";
import { updateSupplierApi } from "../../../../../services/inventory";
import { selectHasModuleAccess } from "../../../../../store/authSlice";

const EditSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasSuppliersWrite = useSelector(selectHasModuleAccess("suppliers", "read-write"));

  // ── GET via Redux ────────────────────────────────────────────────────────
  const { data: supplierResponse, isLoading } = useGetSupplierByIdQuery(id, { skip: !id });
  const supplierData = supplierResponse?.supplier || null;

  const [form, setForm] = useState({ name: "", address: "", gstNumber: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Pre-populate when data arrives ──────────────────────────────────────
  useEffect(() => {
    if (!supplierData) return;
    setForm({
      name: supplierData.name || "",
      address: supplierData.address || "",
      gstNumber: supplierData.gstNumber || "",
    });
  }, [supplierData]);

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Supplier name is required.";
    return errs;
  };

  // ── PATCH via service — no Redux ─────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await updateSupplierApi({
        _id: id,
        name: form.name.trim(),
        address: form.address.trim(),
        gstNumber: form.gstNumber.trim(),
      });
      dispatch(appApi.util.invalidateTags(["Suppliers"]));
      Swal.fire({ icon: "success", title: "Supplier updated!", timer: 1500, showConfirmButton: false });
      navigate("/supplier-list");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update supplier.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasSuppliersWrite) return <Navigate to="/supplier-list" replace />;
  if (isLoading) {
    return (
      <>
        <PageTitle activeMenu={"Edit Supplier"} motherMenu={"Inventory"} />
        <div className="row"><div className="col-12"><p>Loading...</p></div></div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={"Edit Supplier"} motherMenu={"Inventory"} />
      <div className="row">
        <div className="col-xl-12 col-xxl-12 col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Edit Supplier</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="name" className="form-label">
                        Supplier Name*
                      </label>
                      <input
                        id="name"
                        type="text"
                        className="form-control"
                        value={form.name}
                        onChange={set("name")}
                        required
                      />
                      {errors.name && <small className="text-danger">{errors.name}</small>}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label htmlFor="gstNumber" className="form-label">
                        GST Number
                      </label>
                      <input
                        id="gstNumber"
                        type="text"
                        className="form-control"
                        value={form.gstNumber}
                        onChange={set("gstNumber")}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="form-group">
                      <label htmlFor="address" className="form-label">
                        Address
                      </label>
                      <textarea
                        id="address"
                        className="form-control"
                        rows="3"
                        value={form.address}
                        onChange={set("address")}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <button type="submit" className="btn btn-primary me-1" disabled={submitting}>
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger light"
                      onClick={() => navigate("/supplier-list")}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditSupplier;
